// This file is adapted for Monthly view.

import { cn } from '@/lib/utils'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { useShowDataStore } from '.'
import VisibleDateTrackerCell from '@/components/roi/VisibleDateTrackerCell'
import { getCostBaseline, getProfitBaseline } from '@/lib/dr-utils'
import {
  collectBaselineChangesForDate,
} from '@/components/roi/day-roas-utils'
import { renderBaselineChangeTooltipLines } from '@/components/roi/day-roas-columns'
import {
  createMonthlyDayRoasColumn,
  createAllMonthlyDayRoasColumns,
} from '@/components/roi/day-roas-columns'
import useRoiTableStore from '@/store/roi-table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import dayjs from 'dayjs'

// Helper to get all days in a month, format YYYYMMDD
const getDaysInMonth = (monthId) => {
  const month = dayjs(monthId, 'YYYYMM')
  const daysInMonth = month.daysInMonth()
  const days = []
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(month.date(i).format('YYYYMMDD'))
  }
  return days
}

export const columnDate = (country, drBaselines, pidForBaseline, costBaselines, profitBaselines, packageName = null) => ({
  id: 'month',
  accessorFn: (row) => ({ monthId: row.monthId, monthName: row.month }),
  header: 'Month',
  cell: ({ row }) => {
    const { monthId, monthName } = row.getValue()
    const daysInMonth = getDaysInMonth(monthId)
    const representativeDate = daysInMonth[daysInMonth.length - 1]

    const changesInMonth = []
    daysInMonth.forEach((day) => {
      const dateFormatted = dayjs(day, 'YYYYMMDD').format('YYYY-MM-DD')
      const prevDate = dayjs(day, 'YYYYMMDD')
        .subtract(1, 'day')
        .format('YYYYMMDD')

      const changeInfo = collectBaselineChangesForDate({
        dateFormatted,
        dayValue: day,
        prevDateValue: prevDate,
        drBaselines,
        country,
        pidForBaseline,
        packageName,
        costBaselines,
        profitBaselines,
      })

      if (changeInfo) {
        changesInMonth.push(changeInfo)
      }
    })
    const hasChange = changesInMonth.length > 0

    return (
      <VisibleDateTrackerCell date={representativeDate}>
        <div className="relative pl-5">
          {hasChange && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute bottom-1 left-1 w-3 h-3 bg-green-500 rounded-full" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Baseline Changed:</p>
                  {changesInMonth.map((change) => (
                    <div key={change.date}>
                      <strong>On {change.date}:</strong>
                      {renderBaselineChangeTooltipLines(change)}
                    </div>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="text-left">{monthName}</div>
        </div>
      </VisibleDateTrackerCell>
    )
  },
})

// ... (Rest of the columns adapted for monthly data)
// Note: Td3 component needs to be included here if it's used by other columns.

export const Td3 = ({ children, avg, growth, absoluteDiff, formatType = 'number' }) => {
  const formattedGrowth = formatPercent(growth)
  const { line1, line2, line3, line4 } = useShowDataStore()
  
  // 格式化绝对值差异
  let formattedAbsoluteDiff = null
  if (absoluteDiff !== undefined && absoluteDiff !== null && !isNaN(absoluteDiff)) {
    if (formatType === 'usd') {
      formattedAbsoluteDiff = formatUSD(Math.abs(absoluteDiff), 0, 0)
    } else if (formatType === 'percent') {
      formattedAbsoluteDiff = formatPercent(Math.abs(absoluteDiff))
    } else {
      formattedAbsoluteDiff = formatNumber(Math.abs(absoluteDiff), 0, 0)
    }
  }
  
  return (
    <div className="flex flex-col text-right">
      {line1 && <div>{children}</div>}
      {line2 && (
        <div className={cn(line1 ? 'text-primary text-xs' : 'text-base')}>
          {avg}
        </div>
      )}
      {line3 && Number.isFinite(growth) && (
        <div
          className={cn(
            'text-xs',
            growth > 0 ? 'text-primary' : 'text-destructive',
          )}
        >
          {growth > 0 ? <>&uarr;</> : <>&darr;</>}
          {formattedGrowth}
        </div>
      )}
      {line4 && formattedAbsoluteDiff !== null && (
        <div
          className={cn(
            'text-xs',
            absoluteDiff > 0 ? 'text-primary' : absoluteDiff < 0 ? 'text-destructive' : '',
          )}
        >
          {absoluteDiff > 0 ? '+' : absoluteDiff < 0 ? '-' : ''}
          {formattedAbsoluteDiff}
        </div>
      )}
    </div>
  )
}

const bindTd3 = (day) => (drBaselines, country, pid, packageName = null) =>
  createMonthlyDayRoasColumn(day, drBaselines, country, pid, packageName, Td3)

export const columnDay0Roas = bindTd3(0)
export const columnDay3Roas = bindTd3(3)
export const columnDay7Roas = bindTd3(7)
export const columnDay14Roas = bindTd3(14)
export const columnDay30Roas = bindTd3(30)
export const columnDay60Roas = bindTd3(60)
export const columnDay90Roas = bindTd3(90)
export const columnDay120Roas = bindTd3(120)
export const columnDay150Roas = bindTd3(150)

export const createAllMonthlyDayRoasColumnsWithTd3 = (
  drBaselines,
  country,
  pid,
  packageName,
) => createAllMonthlyDayRoasColumns(drBaselines, country, pid, packageName, Td3)

// ... Keep other columns from Monthly/index.jsx, but adapt them to use the new data structure and Td3
// For brevity, I'll show only a few examples

export const columnInstalls = {
  id: 'installs',
  accessorKey: 'installs',
  header: 'Installs',
  cell: ({ row }) => {
    const installs = row.getValue('installs')
    const prevInstalls = row.original.prevInstalls
    const daysInMonth = row.original.daysInMonth
    const monthId = row.original.monthId
    const prevMonth = dayjs(monthId, 'YYYYMM').subtract(1, 'month')
    const prevDaysInMonth = prevMonth.daysInMonth()
    const avg = installs / daysInMonth
    const prevAvg = prevInstalls / prevDaysInMonth
    const growth = (installs - prevInstalls) / prevInstalls
    const absoluteDiff = avg - prevAvg
    return (
      <Td3
        avg={formatNumber(avg, 0, 0)}
        growth={growth}
        absoluteDiff={absoluteDiff}
        formatType="number"
      >
        {formatNumber(installs, 0, 0)}
      </Td3>
    )
  },
}

export const columnRevenue = {
  id: 'revenues',
  accessorKey: 'revenues',
  header: 'Revenues',
  cell: ({ row }) => {
    const revenue = row.getValue('revenues')
    const prevRevenue = row.original.prevRevenues
    const daysInMonth = row.original.daysInMonth
    const monthId = row.original.monthId
    const prevMonth = dayjs(monthId, 'YYYYMM').subtract(1, 'month')
    const prevDaysInMonth = prevMonth.daysInMonth()
    const avg = revenue / daysInMonth
    const prevAvg = prevRevenue / prevDaysInMonth
    const growth = (revenue - prevRevenue) / prevRevenue
    const absoluteDiff = avg - prevAvg
    return (
      <Td3
        avg={formatUSD(avg, 0, 0)}
        growth={growth}
        absoluteDiff={absoluteDiff}
        formatType="usd"
      >
        {formatUSD(revenue, 0, 0)}
      </Td3>
    )
  },
}

const CostsHeader = ({ table, costBaselines, country, pid }) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  let latestDate
  if (visibleDates.size > 0) {
    latestDate = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
  } else {
    const rows = table.getFilteredRowModel().rows
    latestDate = rows.reduce((maxDate, row) => {
      const monthId = row.original.monthId
      const daysInMonth = getDaysInMonth(monthId)
      const currentDate = daysInMonth[daysInMonth.length - 1]
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const headerBaseline = getCostBaseline(costBaselines, country, pid, latestDate)
  const formatted = formatUSD(headerBaseline, 0, 0)

  return (
    <div className="text-right">
      Costs
      {headerBaseline > 0 && (
        <span className="text-xs ml-1">({formatted})</span>
      )}
    </div>
  )
}

export const columnCosts = (costBaselines, country, pid) => ({
  id: 'costs',
  accessorKey: 'costs',
  header: ({ table }) => (
    <CostsHeader
      table={table}
      costBaselines={costBaselines}
      country={country}
      pid={pid}
    />
  ),
  cell: ({ row }) => {
    const costs = row.getValue('costs')
    const prevCosts = row.original.prevCosts
    const daysInMonth = row.original.daysInMonth
    const monthId = row.original.monthId
    const prevMonth = dayjs(monthId, 'YYYYMM').subtract(1, 'month')
    const prevDaysInMonth = prevMonth.daysInMonth()
    const avg = costs / daysInMonth
    const prevAvg = prevCosts / prevDaysInMonth
    const growth = (costs - prevCosts) / prevCosts
    const absoluteDiff = avg - prevAvg
    const daysInMonthArray = getDaysInMonth(monthId)
    const representativeDate = daysInMonthArray[daysInMonthArray.length - 1]
    const cellBaseline = getCostBaseline(
      costBaselines,
      country,
      pid,
      representativeDate,
    )
    const formatted = formatUSD(costs, 0, 0)
    const colorClass =
      cellBaseline > 0
        ? costs < cellBaseline
          ? 'text-destructive'
          : 'text-primary'
        : ''
    return (
      <div className={cn('font-bold text-right', colorClass)}>
        <Td3
          avg={formatUSD(avg, 0, 0)}
          growth={growth}
          absoluteDiff={absoluteDiff}
          formatType="usd"
        >
          {formatted}
        </Td3>
      </div>
    )
  },
})

const ProfitHeader = ({ table, profitBaselines, country, pid }) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  let latestDate
  if (visibleDates.size > 0) {
    latestDate = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
  } else {
    const rows = table.getFilteredRowModel().rows
    latestDate = rows.reduce((maxDate, row) => {
      const monthId = row.original.monthId
      const daysInMonth = getDaysInMonth(monthId)
      const currentDate = daysInMonth[daysInMonth.length - 1]
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const headerBaseline = getProfitBaseline(
    profitBaselines,
    country,
    pid,
    latestDate,
  )
  const formatted = formatUSD(headerBaseline, 0, 0)

  return (
    <div className="text-right">
      Profit
      {headerBaseline !== 0 && (
        <span className="text-xs ml-1">({formatted})</span>
      )}
    </div>
  )
}

export const columnProfit = (profitBaselines, country, pid) => ({
  id: 'profit',
  accessorKey: 'profit',
  header: ({ table }) => (
    <ProfitHeader
      table={table}
      profitBaselines={profitBaselines}
      country={country}
      pid={pid}
    />
  ),
  cell: ({ row }) => {
    const profit = row.getValue('profit')
    const prevProfit = row.original.prevProfit
    const daysInMonth = row.original.daysInMonth
    const monthId = row.original.monthId
    const prevMonth = dayjs(monthId, 'YYYYMM').subtract(1, 'month')
    const prevDaysInMonth = prevMonth.daysInMonth()
    const avg = profit / daysInMonth
    const prevAvg = prevProfit / prevDaysInMonth
    const growth = (profit - prevProfit) / prevProfit
    const absoluteDiff = avg - prevAvg
    const daysInMonthArray = getDaysInMonth(monthId)
    const representativeDate = daysInMonthArray[daysInMonthArray.length - 1]
    const cellBaseline = getProfitBaseline(
      profitBaselines,
      country,
      pid,
      representativeDate,
    )
    const formatted = formatUSD(profit, 0, 0)
    const colorClass =
      cellBaseline !== 0
        ? profit < cellBaseline
          ? 'text-destructive'
          : 'text-primary'
        : ''
    return (
      <div className={cn('font-bold text-right', colorClass)}>
        <Td3
          avg={formatUSD(avg, 0, 0)}
          growth={growth}
          absoluteDiff={absoluteDiff}
          formatType="usd"
        >
          {formatted}
        </Td3>
      </div>
    )
  },
})

export const columnEr = (erBaseLine) => ({
  id: 'er',
  accessorKey: 'er',
  header: () => (
    <div>
      ER<span className="text-xs">({formatPercent(erBaseLine)})</span>
    </div>
  ),
  cell: ({ row }) => {
    const er = row.getValue('er') / 100
    const prevEr = row.original.prevEr / 100
    const growth = (er - prevEr) / prevEr
    const absoluteDiff = er - prevEr
    const formatted = formatPercent(er)
    return (
      <Td3 avg="-" growth={growth} absoluteDiff={absoluteDiff} formatType="percent">
        {formatted}
      </Td3>
    )
  },
})

export const columnCPI = {
  id: 'cpi',
  accessorKey: 'cpi',
  header: 'CPI(DNU)',
  cell: ({ row }) => {
    const cpi = row.getValue('cpi')
    const prevCpi = row.original.prevCpi
    const growth = (cpi - prevCpi) / prevCpi
    const absoluteDiff = cpi - prevCpi
    const formatted = formatUSD(cpi, 4, 4)
    return (
      <Td3 avg="-" growth={growth} absoluteDiff={absoluteDiff} formatType="usd">
        {formatted}
      </Td3>
    )
  },
}

// CPI(Installs) is not present in Monthly view, so I'll omit it.

// The SimpleGoalsColumn is already in Monthly/index.jsx, so it does not need to be here.
